import { z } from 'zod';
import { defineForm } from '../registry.js';
import { CONFIRMERS, evidenceFields, longtext } from './common.js';

// アセスメント（厚労省 課題分析標準項目 23 項目・令和5年10月改正 Vol.1179）。
// 旧: apps/api/src/assessment-items.ts（正本）。Python の写し agent/care_agent/assessment_items.py は同じ値

export type AssessmentCategory = 'basic' | 'analysis'; // basic=基本情報(№1-9) / analysis=課題分析(№10-23)
export type AssessmentInputType = 'textarea' | 'select' | 'nested';

// 選択肢（値＝ラベル文字列）
export const DISABILITY_ADL = ['自立', 'J1', 'J2', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;
export const DEMENTIA_ADL = ['自立', 'Ⅰ', 'Ⅱa', 'Ⅱb', 'Ⅲa', 'Ⅲb', 'Ⅳ', 'M'] as const;
export const CARE_LEVEL = ['非該当', '要支援1', '要支援2', '要介護1', '要介護2', '要介護3', '要介護4', '要介護5'] as const;
export const ADL_SUPPORT = ['自立', '見守り', '一部介助', '全介助'] as const;
export const ASSESS_REASON = ['初回', '更新', '区分変更', '状態変化', 'サービス変更', 'その他'] as const;

type Options = readonly [string, ...string[]];

export interface AssessmentItemChild {
  subCode: string;
  name: string;
  options: Options;
}

export interface AssessmentItemMaster {
  itemCode: string;
  name: string;
  category: AssessmentCategory;
  inputType: AssessmentInputType;
  options?: Options; // select
  children?: readonly AssessmentItemChild[]; // nested
}

export const ADL_CHILDREN = [
  { subCode: 'eat', name: '食事', options: ADL_SUPPORT },
  { subCode: 'posture', name: '起居動作', options: ADL_SUPPORT },
  { subCode: 'transfer', name: '移乗', options: ADL_SUPPORT },
  { subCode: 'move', name: '移動', options: ADL_SUPPORT },
  { subCode: 'excretion', name: '排泄', options: ADL_SUPPORT },
  { subCode: 'bath', name: '入浴', options: ADL_SUPPORT },
  { subCode: 'dress', name: '更衣', options: ADL_SUPPORT },
  { subCode: 'grooming', name: '整容', options: ADL_SUPPORT },
] as const satisfies readonly AssessmentItemChild[];

export const IADL_CHILDREN = [
  { subCode: 'cook', name: '調理', options: ADL_SUPPORT },
  { subCode: 'clean', name: '掃除', options: ADL_SUPPORT },
  { subCode: 'laundry', name: '洗濯', options: ADL_SUPPORT },
  { subCode: 'shop', name: '買物', options: ADL_SUPPORT },
  { subCode: 'money', name: '金銭管理', options: ADL_SUPPORT },
  { subCode: 'medication', name: '服薬管理', options: ADL_SUPPORT },
  { subCode: 'transport', name: '交通機関の利用', options: ADL_SUPPORT },
  { subCode: 'phone', name: '電話', options: ADL_SUPPORT },
] as const satisfies readonly AssessmentItemChild[];

export const ASSESSMENT_ITEMS = [
  { itemCode: '01', name: '基本情報（受付・利用者等基本情報）', category: 'basic', inputType: 'textarea' },
  { itemCode: '02', name: 'これまでの生活と現在の状況', category: 'basic', inputType: 'textarea' },
  { itemCode: '03', name: '利用者の社会保障制度の利用情報', category: 'basic', inputType: 'textarea' },
  { itemCode: '04', name: '現在利用している支援や社会資源の状況', category: 'basic', inputType: 'textarea' },
  { itemCode: '05', name: '日常生活自立度（障害）', category: 'basic', inputType: 'select', options: DISABILITY_ADL },
  { itemCode: '06', name: '日常生活自立度（認知症）', category: 'basic', inputType: 'select', options: DEMENTIA_ADL },
  { itemCode: '07', name: '主訴・意向', category: 'basic', inputType: 'textarea' },
  { itemCode: '08', name: '認定情報', category: 'basic', inputType: 'select', options: CARE_LEVEL },
  { itemCode: '09', name: '今回のアセスメントの理由', category: 'basic', inputType: 'select', options: ASSESS_REASON },
  { itemCode: '10', name: '健康状態', category: 'analysis', inputType: 'textarea' },
  { itemCode: '11', name: 'ADL', category: 'analysis', inputType: 'nested', children: ADL_CHILDREN },
  { itemCode: '12', name: 'IADL', category: 'analysis', inputType: 'nested', children: IADL_CHILDREN },
  { itemCode: '13', name: '認知機能や判断能力', category: 'analysis', inputType: 'textarea' },
  { itemCode: '14', name: 'コミュニケーションにおける理解と表出の状況', category: 'analysis', inputType: 'textarea' },
  { itemCode: '15', name: '生活リズム', category: 'analysis', inputType: 'textarea' },
  { itemCode: '16', name: '排泄の状況', category: 'analysis', inputType: 'select', options: ADL_SUPPORT },
  { itemCode: '17', name: '清潔の保持に関する状況', category: 'analysis', inputType: 'select', options: ADL_SUPPORT },
  { itemCode: '18', name: '口腔内の状況', category: 'analysis', inputType: 'textarea' },
  { itemCode: '19', name: '食事摂取の状況', category: 'analysis', inputType: 'select', options: ADL_SUPPORT },
  { itemCode: '20', name: '社会との関わり', category: 'analysis', inputType: 'textarea' },
  { itemCode: '21', name: '家族等の状況', category: 'analysis', inputType: 'textarea' },
  { itemCode: '22', name: '居住環境', category: 'analysis', inputType: 'textarea' },
  { itemCode: '23', name: 'その他留意すべき事項・状況', category: 'analysis', inputType: 'textarea' },
] as const satisfies readonly AssessmentItemMaster[];

export const ASSESSMENT_ITEM_CODES = ASSESSMENT_ITEMS.map((item) => item.itemCode);
export type AssessmentItemCode = (typeof ASSESSMENT_ITEMS)[number]['itemCode'];

// 1 項目の本文。旧 AssessmentItemRecord の text / choice / subChoices / reason / evidenceEventIds。
// text は textarea の本文、select・nested では補足。
// 長さの上限は旧 API に無い（Python のエージェントだけ text 500・reason 300 で切っていた）。ここは API 側に合わせ上限なし
const hasValue = (v: { text?: string; choice?: string; subChoices?: Record<string, string | undefined> }) =>
  (v.text !== undefined && v.text.trim().length > 0) ||
  (v.choice !== undefined && v.choice.length > 0) ||
  (v.subChoices !== undefined && Object.values(v.subChoices).some((x) => x !== undefined));

function itemSchema(item: AssessmentItemMaster) {
  const text = longtext(item.inputType === 'textarea' ? '本文' : '補足').optional();
  if (item.inputType === 'select') {
    return z
      .object({ text, choice: z.enum(item.options!).optional().describe('選択'), ...evidenceFields })
      .strict()
      .refine(hasValue, { message: 'アセスメント項目に値がありません' });
  }
  if (item.inputType === 'nested') {
    const shape = Object.fromEntries(item.children!.map((c) => [c.subCode, z.enum(c.options).optional().describe(c.name)]));
    return z
      .object({ text, subChoices: z.object(shape).strict().optional().describe('細目'), ...evidenceFields })
      .strict()
      .refine(hasValue, { message: 'アセスメント項目に値がありません' });
  }
  return z
    .object({ text, ...evidenceFields })
    .strict()
    .refine(hasValue, { message: 'アセスメント項目に値がありません' });
}

// 本文 = 項目コード（'01'〜'23'）→ 項目の値。下書きは一部の項目だけでよい（未記入の項目は省く）。
// '10' 以降は JS のキー順で先に来るので、表示順は meta の order で持つ
const itemShape = Object.fromEntries(
  ASSESSMENT_ITEMS.map((item, i) => [
    item.itemCode,
    itemSchema(item).optional().meta({ order: i }).describe(`${item.itemCode} ${item.name}`),
  ]),
) as Record<AssessmentItemCode, z.ZodOptional<ReturnType<typeof itemSchema>>>;

export const assessmentSchema = z
  .object(itemShape)
  .strict()
  .refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'アセスメントは少なくとも 1 項目が必要です' });

export type AssessmentItemBody = {
  text?: string;
  choice?: string;
  subChoices?: Record<string, string | undefined>;
  reason?: string;
  evidenceEventIds?: string[];
};
export type AssessmentBody = Partial<Record<AssessmentItemCode, AssessmentItemBody>>;

export const assessmentForm = defineForm({
  app: 'caremanager',
  docType: 'assessment',
  name: 'アセスメント',
  version: 1,
  schema: assessmentSchema,
  confirmers: CONFIRMERS,
  description: '課題分析標準項目 23 項目。確定すると第2表・第3表の見直し提案の起点になる',
});
